import {rootStore} from "stores/rootStore";
import {reaction, runInAction} from "mobx";
import {Sentence} from "types";
import type {EditorCut, EditorStore} from "stores/editorStore";

// Handle selected file change
reaction(
    () => [rootStore.batchFile.selectedFile, rootStore.batchFile.responseCounter],
    () => runInAction(() => {
        const batchFile = rootStore.batchFile;
        const plagiarism = rootStore.plagiarism;
        const input = rootStore.input;
        const aiScan = rootStore.aiScan;

        const uuid = batchFile.selectedFile;
        if (!uuid) return;
        if (!batchFile.filesWaiting.has(uuid)) return;
        let state = batchFile.filesWaiting.get(uuid)!;
        const fileInfo = batchFile.filesResults.get(uuid)!;
        const noPlagiarismResult = state?.plagiarism === "not_requested";
        // A "Scan first N" run: its results cover the part before the cut, the rest shows unscanned.
        // `cut` stays while the whole document runs (the divider says so); `settled` is the result's.
        const cut = state?.partial?.cut ?? null;
        const settled = cut?.scanning ? null : cut;
        const showText = (editor: EditorStore, text: string, shownCut: EditorCut | null) => shownCut
            ? editor.setText(shownCut.text, shownCut, {scanning: shownCut.scanning})
            : editor.setText(text);

        if (state?.plagiarism === 'ready') {
            plagiarism.setIsLoading(false);
            showText(plagiarism.editor, fileInfo.text, settled);
            plagiarism.setPlagiarismData(fileInfo.plagiarism!);
            plagiarism.setPlagiarismSegments(fileInfo?.plagiarism_segments!);
        } else if (state?.plagiarism === 'waiting') {
            plagiarism.setIsLoading(true);
            plagiarism.setPlagiarismData(null);
            plagiarism.setScore(null);
            // The document itself, until the check colours it.
            showText(plagiarism.editor, fileInfo?.text || "", cut);
        } else if (state?.plagiarism === 'error') {
            plagiarism.setIsLoading(false);
            plagiarism.setPlagiarismData(null);
            plagiarism.setScore(null);
            showText(plagiarism.editor, fileInfo?.text || "", null);
        } else if (noPlagiarismResult) {
            // No plagiarism result for this file — clear so it doesn't show the
            // previously selected file's result.
            plagiarism.setIsLoading(false);
            plagiarism.setPlagiarismData(null);
            plagiarism.setScore(null);
            plagiarism.editor.clear();
        }

        const grammarStore = input.grammarScanStore;
        if (state?.grammar === 'ready' && fileInfo?.grammar) {
            // Task payload carries text + matches + language — same shape the
            // text-scan flow feeds into the store. A cut goes first: load appends the rest after it.
            if (settled) grammarStore.editor.setText(settled.text, settled);
            else if (grammarStore.editor.cut) grammarStore.editor.clear();
            grammarStore.load(fileInfo.grammar);
            // Grammar-only file scan: nothing else fills the "Scanned on" line.
            if (state?.scan === 'not_requested' && noPlagiarismResult) {
                input.setScannedOn(state.scannedOn);
            }
        } else if (state?.grammar === 'waiting') {
            // The previous file's checked text would stay on screen otherwise: the Grammar
            // view shows the store's working text as long as there is one.
            grammarStore.clear();
            grammarStore.setLoading(true);
        } else if (state?.grammar === 'error' || state?.grammar === 'not_requested') {
            // Clear so the previously selected file's suggestions don't linger.
            grammarStore.clear();
        }

        // The tab shows this file's analysis, once its scan has landed — what its Humanize
        // text goes by. The certificate reads the batch itself: its pending spans both checks.
        aiScan.setAnalysis(null, state?.scan === 'ready' ? fileInfo?.analysis_uuid ?? null : null);
        if (state?.scan === 'ready') {
            aiScan.setIsLoading(false);
            input.setScannedOn(state.scannedOn);
            aiScan.setScannedText(fileInfo?.text || "");
            aiScan.setScanMode(fileInfo?.deep_scan ? 'deep' : 'simple');
            if (typeof fileInfo?.ai_score === "number") {
                aiScan.setScore(fileInfo.ai_score);
                aiScan.setMetrics(fileInfo);
            } else {
                // A task that ended in `error` lands as "ready" too, with no score — never
                // leave the previously selected file's verdict standing for this one.
                aiScan.setScore(null);
            }
            showText(aiScan.editor, fileInfo?.text || "", settled);
            if (!!fileInfo?.segments?.length) {
                aiScan.showSegments(aiScan.mapAIScanSegments(fileInfo?.segments as Sentence[]));
            }
        } else if (state?.scan === 'waiting') {
            aiScan.setIsLoading(true);
            aiScan.setScore(null);
            aiScan.setScannedText("");
            // The document itself, until the verdict colours it.
            showText(aiScan.editor, fileInfo?.text || "", cut);
        } else if (state?.scan === 'error') {
            aiScan.setIsLoading(false);
            aiScan.setScore(null);
            aiScan.editor.setText(fileInfo?.text || "");
        } else if (state?.scan === "not_requested") {
            // No AI result for this file: its plain text, never the previously selected
            // file's verdict — the Grammar tab reads this editor while its own check runs.
            aiScan.setIsLoading(false);
            aiScan.setScore(null);
            aiScan.setScannedText("");
            aiScan.editor.setText(fileInfo?.text || "");
        }

        // The inline error banner belongs to the selected file: show this file's own scan
        // failure, clear it otherwise. Previously the error lived in the shared input.error,
        // so a failed file's banner bled onto a different, successfully-scanned file.
        // Guard on errorType so re-runs (responseCounter bumps while a batch finishes) don't
        // re-fire the error analytics event for an already-shown error.
        const hasError = state?.scan === 'error' || state?.plagiarism === 'error' || state?.grammar === 'error';
        if (hasError) {
            const code = state.error?.code ?? '';
            if (input.errorType !== code) input.setError(state.error);
        } else if (input.error) {
            input.setError();
        }
    })
);

// An anon's preview ends at a login divider written into the markup: a login without a reload
// has to redraw the sentences whole.
reaction(
    () => rootStore.user.isAnonymous,
    (anon) => {
        const {aiScan} = rootStore;
        if (!anon && aiScan.editor.previewShown) aiScan.showSegments(aiScan.editor.segments);
    }
);
